/**
 * RitualWhoopInsights - Shows WHOOP correlation data for rituals
 * Displays how ritual completion correlates with biometric outcomes
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Activity, Heart, Moon, TrendingUp, TrendingDown, 
  Sparkles, Zap, Info
} from 'lucide-react';
import { useRitualWhoopCorrelation } from '@/hooks/useRitualWhoopCorrelation';
import { useWhoopConnection } from '@/hooks/useWhoopDataCached';
import { cn } from '@/lib/utils';

interface RitualWhoopInsightsProps {
  className?: string;
  compact?: boolean;
}

export const RitualWhoopInsights: React.FC<RitualWhoopInsightsProps> = ({
  className,
  compact = false
}) => {
  const { data: connectionInfo } = useWhoopConnection();
  const { data: correlation, isLoading } = useRitualWhoopCorrelation(30);

  if (!connectionInfo?.isConnected) {
    return null; // Don't show if WHOOP not connected
  }

  if (isLoading) {
    return (
      <Card className={cn("border-border/50 animate-pulse", className)}>
        <CardContent className="p-4">
          <div className="h-16 bg-muted/20 rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  if (!correlation || !correlation.hasSignificantData) {
    // Show encouragement to build data
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={className}
      >
        <Card className="border-border/50 bg-gradient-to-br from-primary/5 to-secondary/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Activity className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground mb-1">
                  WHOOP × Healthy Habits
                </p>
                <p className="text-xs text-muted-foreground">
                  {correlation?.insightMessage || "Complete habits to see how they impact your biometrics."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  if (compact) {
    // Compact single-line insight
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={className}
      >
        <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
          <Sparkles className="w-4 h-4 text-primary shrink-0" />
          <p className="text-sm text-foreground">
            {correlation.insightMessage}
          </p>
        </div>
      </motion.div>
    );
  }

  // Full insights card
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={className}
    >
      <Card className="border-border/50 overflow-hidden">
        <CardContent className="p-4 space-y-4">
          {/* Header with main insight */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center shrink-0">
              <Activity className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">
                WHOOP × Healthy Habits Correlation
              </p>
              <p className="text-xs text-muted-foreground">
                {correlation.insightMessage}
              </p>
            </div>
          </div>

          {/* Correlation metrics */}
          <div className="grid grid-cols-3 gap-3">
            {/* Recovery */}
            {correlation.recoveryImprovementPercent !== null && (
              <div className="p-3 rounded-lg bg-muted/20">
                <div className="flex items-center gap-1 mb-1">
                  <Heart className="w-3 h-3 text-secondary" />
                  <span className="text-xs text-muted-foreground">Recovery</span>
                </div>
                <div className="flex items-center gap-1">
                  {correlation.recoveryImprovementPercent > 0 ? (
                    <TrendingUp className="w-4 h-4 text-primary" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-destructive" />
                  )}
                  <span className={cn(
                    "text-lg font-semibold",
                    correlation.recoveryImprovementPercent > 0 ? "text-primary" : "text-destructive"
                  )}>
                    {correlation.recoveryImprovementPercent > 0 ? '+' : ''}{correlation.recoveryImprovementPercent}%
                  </span>
                </div>
              </div>
            )}

            {/* HRV */}
            {correlation.hrvImprovementPercent !== null && (
              <div className="p-3 rounded-lg bg-muted/20">
                <div className="flex items-center gap-1 mb-1">
                  <Zap className="w-3 h-3 text-primary" />
                  <span className="text-xs text-muted-foreground">HRV</span>
                </div>
                <div className="flex items-center gap-1">
                  {correlation.hrvImprovementPercent > 0 ? (
                    <TrendingUp className="w-4 h-4 text-primary" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-destructive" />
                  )}
                  <span className={cn(
                    "text-lg font-semibold",
                    correlation.hrvImprovementPercent > 0 ? "text-primary" : "text-destructive"
                  )}>
                    {correlation.hrvImprovementPercent > 0 ? '+' : ''}{correlation.hrvImprovementPercent}%
                  </span>
                </div>
              </div>
            )}

            {/* Sleep */}
            {correlation.sleepImprovementPercent !== null && (
              <div className="p-3 rounded-lg bg-muted/20">
                <div className="flex items-center gap-1 mb-1">
                  <Moon className="w-3 h-3 text-accent" />
                  <span className="text-xs text-muted-foreground">Sleep</span>
                </div>
                <div className="flex items-center gap-1">
                  {correlation.sleepImprovementPercent > 0 ? (
                    <TrendingUp className="w-4 h-4 text-primary" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-destructive" />
                  )}
                  <span className={cn(
                    "text-lg font-semibold",
                    correlation.sleepImprovementPercent > 0 ? "text-primary" : "text-destructive"
                  )}>
                    {correlation.sleepImprovementPercent > 0 ? '+' : ''}{correlation.sleepImprovementPercent}%
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Ritual completion stats */}
          <div className="pt-2 border-t border-border/30">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
              <span>Last 30 days habit completion</span>
              <span className="font-medium text-foreground">
                {correlation.daysWithBothRituals + correlation.daysWithMorningOnly + correlation.daysWithEveningOnly} active days
              </span>
            </div>
            <div className="flex gap-1">
              <Badge variant="outline" className="text-xs gap-1">
                <span className="w-2 h-2 rounded-full bg-primary" />
                {correlation.daysWithBothRituals} both
              </Badge>
              <Badge variant="outline" className="text-xs gap-1">
                <span className="w-2 h-2 rounded-full bg-secondary" />
                {correlation.daysWithMorningOnly} morning
              </Badge>
              <Badge variant="outline" className="text-xs gap-1">
                <span className="w-2 h-2 rounded-full bg-accent" />
                {correlation.daysWithEveningOnly} evening
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default RitualWhoopInsights;
